import { useEffect, useState } from 'react';
import { KeyRound, Check, X } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

interface Provider {
  name: string;
  configured: boolean;
  client_id_present: boolean;
  client_secret_present: boolean;
  redirect_url: string | null;
}

export function SsoPage() {
  const [providers, setProviders] = useState<Provider[] | null>(null);

  useEffect(() => {
    api<{ data: Provider[] }>('/admin/sso-providers')
      .then((r) => setProviders(r.data))
      .catch(() => setProviders([]));
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">SSO providers</h1>
        <p className="text-sm text-muted-foreground">
          Client IDs and secrets live in Azure Key Vault. This panel shows which providers are
          currently configured. To rotate or add credentials, update the secret in Key Vault and
          redeploy.
        </p>
      </header>

      {providers === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
      {providers !== null && (
        <div className="grid gap-4 sm:grid-cols-2">
          {providers.map((p) => (
            <Card key={p.name}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="capitalize">{p.name}</CardTitle>
                {p.configured ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-900">
                    <Check className="h-3 w-3" /> Configured
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-900">
                    <X className="h-3 w-3" /> Not configured
                  </span>
                )}
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Client ID" present={p.client_id_present} />
                <Row label="Client secret" present={p.client_secret_present} />
                <Row
                  label="Redirect URL"
                  present={p.redirect_url !== null && p.redirect_url !== ''}
                  value={p.redirect_url ?? '—'}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <KeyRound className="mr-2 inline h-5 w-5" /> Key Vault references
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          The expected Key Vault secret names are{' '}
          <code className="font-mono">SOCIALITE_GOOGLE_CLIENT_ID</code> /{' '}
          <code className="font-mono">_SECRET</code> and the same shape for <code>MICROSOFT</code>,{' '}
          <code>FACEBOOK</code>, <code>LINKEDIN</code>. Set them as Container App secret references;
          the framework reads them via <code className="font-mono">config('services.*')</code>.
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, present, value }: { label: string; present: boolean; value?: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      {value !== undefined ? (
        <span className="truncate text-right font-mono text-xs">{value}</span>
      ) : present ? (
        <Check className="h-4 w-4 text-emerald-600" />
      ) : (
        <X className="h-4 w-4 text-amber-600" />
      )}
    </div>
  );
}
